// @ts-ignore - package.json import for version
import packageJson from '../../package.json';

const VERSION_STORAGE_KEY = 'nimax_app_version';

/**
 * Get the current application version
 * Priority:
 * 1. Environment variable (set during build)
 * 2. Fallback to package.json version with -local suffix
 *
 * @returns The application version string
 */
export const getAppVersion = (): string => {
  // First try to get version from environment variable
  const envVersion = process.env.REACT_APP_VERSION;

  if (envVersion) {
    return envVersion;
  }

  // Fallback to package.json version (for development)
  try {
    return `${packageJson.version}-local`;
  } catch (error) {
    console.error('Error loading version from package.json:', error);
    return '0.0.0-unknown';
  }
};

/**
 * Get the stored version from localStorage
 * @returns The stored version or null if not found
 */
export const getStoredVersion = (): string | null => {
  try {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  } catch (error) {
    console.error('Error reading stored version:', error);
    return null;
  }
};

/**
 * Save the current version to localStorage
 */
export const saveCurrentVersion = (): void => {
  try {
    const currentVersion = getAppVersion();
    localStorage.setItem(VERSION_STORAGE_KEY, currentVersion);
  } catch (error) {
    console.error('Error saving version to localStorage:', error);
  }
};

/**
 * Clear the stored version from localStorage
 */
export const clearStoredVersion = (): void => {
  try {
    localStorage.removeItem(VERSION_STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing stored version:', error);
  }
};

/**
 * Check if the app version has changed since the last login
 * @returns true if version changed (logout needed), false if same version
 */
export const hasVersionChanged = (): boolean => {
  const storedVersion = getStoredVersion();
  const currentVersion = getAppVersion();

  // If no stored version but user is authenticated, force logout
  // This handles the case where user logged in before version tracking was implemented
  if (!storedVersion) {
    return true;
  }

  // Compare versions
  return storedVersion !== currentVersion;
};

export { VERSION_STORAGE_KEY };
